import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteBannerService } from '@hooks/services/useSiteBannerService';
import { localSettings } from '@utils/localSettings';

const SITE_BANNER_PERMANENT_DISMISSALS_KEY = 'siteBannerPermanentDismissalKeys';

function getSessionDismissalKey(banner) {
    return `siteBanner.dismissed.${banner.id}.${banner.revision}`;
}

function getPermanentDismissalKey(banner) {
    return `${banner.id}.${banner.revision}`;
}

function isSessionDismissed(banner) {
    if (banner.repeat_policy !== 'session') {
        return false;
    }
    return sessionStorage.getItem(getSessionDismissalKey(banner)) === 'true';
}

function validateBannerId(bannerId) {
    if (!Number.isInteger(bannerId) || bannerId < 1) {
        throw new Error('bannerId must be a positive integer');
    }
}

function getPermanentDismissals() {
    const preference = localSettings.get(SITE_BANNER_PERMANENT_DISMISSALS_KEY);
    if (preference === null) {
        return { bannerKeys: [] };
    }
    if (!Array.isArray(preference.bannerKeys)) {
        throw new Error('siteBannerPermanentDismissalKeys.bannerKeys must be an array');
    }
    preference.bannerKeys.forEach((bannerKey) => {
        if (typeof bannerKey !== 'string' || bannerKey.trim() === '') {
            throw new Error('siteBannerPermanentDismissalKeys.bannerKeys must contain non-empty strings');
        }
    });
    return preference;
}

function isPermanentlyDismissed(banner) {
    const preference = getPermanentDismissals();
    return preference.bannerKeys.includes(getPermanentDismissalKey(banner));
}

function addPermanentDismissal(banner) {
    validateBannerId(banner.id);
    const preference = getPermanentDismissals();
    const bannerKeys = Array.from(new Set([...preference.bannerKeys, getPermanentDismissalKey(banner)]));
    localSettings.set(SITE_BANNER_PERMANENT_DISMISSALS_KEY, { bannerKeys });
}

export const useSiteBannerLogic = () => {
    const location = useLocation();
    const { getActiveSiteBanner, dismissSiteBanner } = useSiteBannerService();
    const [banner, setBanner] = useState(null);
    const [error, setError] = useState(null);
    const path = location.pathname;

    const shouldRequestBanner = useMemo(() => path.startsWith('/app/'), [path]);

    const loadBanner = useCallback(async () => {
        if (!shouldRequestBanner) {
            setBanner(null);
            return;
        }

        const activeBanner = await getActiveSiteBanner(path);
        if (activeBanner !== null && isPermanentlyDismissed(activeBanner)) {
            setBanner(null);
            return;
        }
        if (activeBanner !== null && isSessionDismissed(activeBanner)) {
            setBanner(null);
            return;
        }

        setBanner(activeBanner);
    }, [getActiveSiteBanner, path, shouldRequestBanner]);

    useEffect(() => {
        setError(null);
        loadBanner().catch((loadError) => {
            setError(loadError);
            setBanner(null);
        });
    }, [loadBanner]);

    const dismissBanner = useCallback(async (bannerId) => {
        if (banner === null || banner.id !== bannerId) {
            throw new Error('Cannot dismiss a banner that is not active');
        }

        await dismissSiteBanner(bannerId);
        if (banner.repeat_policy === 'session') {
            sessionStorage.setItem(getSessionDismissalKey(banner), 'true');
        }
        setBanner(null);
    }, [banner, dismissSiteBanner]);

    const neverShowBannerAgain = useCallback((bannerId) => {
        if (banner === null || banner.id !== bannerId) {
            throw new Error('Cannot permanently hide a banner that is not active');
        }
        if (banner.allow_permanent_dismissal !== true) {
            throw new Error('Site banner does not allow permanent dismissal');
        }

        addPermanentDismissal(banner);
        setBanner(null);
    }, [banner]);

    return {
        banner,
        error,
        loadBanner,
        dismissBanner,
        neverShowBannerAgain
    };
};

export default useSiteBannerLogic;
